import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Button,
    TextField,
    List,
    ListItem,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Grid,
} from '@mui/material';
import api from './AxiosConfig';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Loading from "../Loading";

const ManageWaiters = ({ token, baseUrl, onBack }) => {
    const [showAddWaiterForm, setShowAddWaiterForm] = useState(false);
    const [waiters, setWaiters] = useState([]);
    const [waiterData, setWaiterData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        confirmPassword: '',
    });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [waiterToDelete, setWaiterToDelete] = useState(null);
    const [passwordValid, setPasswordValid] = useState({
        length: false,
        digit: false,
        special: false,
        uppercase: false,
    });
    const [loading, setLoading] = useState(false);

    const fetchWaiters = async () => {
        setLoading(true);
        try {
            const response = await api.get(`${baseUrl}/api/restaurant/getWaiters`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setWaiters(response.data);
        } catch (error) {
            alert('Błąd przy pobieraniu listy kelnerów');
        } finally {
            setLoading(false)
        }
    };

    useEffect(() => {
        fetchWaiters();
    }, []);

    useEffect(() => {
        const validations = {
            length: waiterData.password.length >= 6,
            digit: /\d/.test(waiterData.password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(waiterData.password),
            uppercase: /[A-Z]/.test(waiterData.password),
        };
        setPasswordValid(validations);
    }, [waiterData.password]);

    const handleWaiterDataChange = (e) => {
        const { name, value } = e.target;
        setWaiterData({ ...waiterData, [name]: value });
    };

    const clearWaiterForm = () => {
        setWaiterData({
            email: '',
            firstName: '',
            lastName: '',
            password: '',
            confirmPassword: '',
        });
    };

    // Helper function for email validation
    const isValidEmail = (email) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const handleRegisterWaiter = async () => {
        // Validate email format first
        if (!isValidEmail(waiterData.email)) {
            alert('Niepoprawny email');
            return;
        }

        // Validate that first name and last name are not empty
        if (!waiterData.firstName.trim() || !waiterData.lastName.trim()) {
            alert('Imię i nazwisko nie mogą być puste');
            return;
        }

        if (waiterData.password !== waiterData.confirmPassword) {
            alert('Hasła nie są zgodne');
            return;
        }
        if (!passwordValid.length || !passwordValid.digit || !passwordValid.special || !passwordValid.uppercase) {
            alert('Hasło nie spełnia wymagań (min. 6 znaków, 1 cyfra, 1 znak specjalny, 1 duża litera)');
            return;
        }
        setLoading(true);
        try {
            await api.post(
                `${baseUrl}/api/restaurant/registerWaiter`,
                {
                    username: waiterData.email,
                    password: waiterData.password,
                    firstName: waiterData.firstName,
                    lastName: waiterData.lastName,
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            alert('Kelner dodany pomyślnie');
            clearWaiterForm();
            setShowAddWaiterForm(false);
            fetchWaiters();
        } catch (error) {
            alert('Błąd przy dodawaniu kelnera');
        } finally {
            setLoading(false)
        }
    };

    const handleDeleteWaiterClick = (waiterId) => {
        setWaiterToDelete(waiterId);
        setDeleteDialogOpen(true);
    };

    const handleDeleteWaiterConfirm = async () => {
        setLoading(true);
        try {
            await api.post(
                `${baseUrl}/api/restaurant/deleteWaiter?waiterId=${waiterToDelete}`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            setDeleteDialogOpen(false);
            fetchWaiters();
        } catch (error) {
            alert('Błąd przy usuwaniu kelnera');
        } finally {
            setLoading(false)
        }
    };

    const handleDeleteDialogClose = () => {
        setDeleteDialogOpen(false);
        setWaiterToDelete(null);
    };

    return (
        <Container sx={{ mt: 4, mb: 4 }}>
            <Loading open={loading} />
            <Box display="flex" alignItems="center" mb={3}>
                <IconButton
                    onClick={() => {
                        if (showAddWaiterForm) {
                            setShowAddWaiterForm(false);
                        } else {
                            onBack();
                        }
                    }}
                    sx={{ mr: 2 }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4">Zarządzaj Kelnerami</Typography>
            </Box>

            {!showAddWaiterForm ? (
                <>
                    <Box mb={2}>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowAddWaiterForm(true)}>
                            Dodaj Kelnera
                        </Button>
                    </Box>
                    <Paper elevation={3} sx={{ p: 3 }}>
                        <Typography variant="h5" sx={{ mb: 2 }}>
                            Lista Kelnerów
                        </Typography>
                        <List>
                            {waiters.map((waiter) => (
                                <ListItem key={waiter.id} divider>
                                    <ListItemText primary={`${waiter.firstName} ${waiter.lastName} (${waiter.username})`} />
                                    <IconButton color="error" onClick={() => handleDeleteWaiterClick(waiter.id)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </>
            ) : (
                <Paper elevation={3} sx={{ p: 3 }}>
                    <Typography variant="h5" align="center" sx={{ mb: 2 }}>
                        Dodaj Kelnera
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField label="Email" name="email" fullWidth value={waiterData.email} onChange={handleWaiterDataChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Imię" name="firstName" fullWidth value={waiterData.firstName} onChange={handleWaiterDataChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Nazwisko" name="lastName" fullWidth value={waiterData.lastName} onChange={handleWaiterDataChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Hasło" name="password" type="password" fullWidth value={waiterData.password} onChange={handleWaiterDataChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Powtórz hasło" name="confirmPassword" type="password" fullWidth value={waiterData.confirmPassword} onChange={handleWaiterDataChange} />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ p: 1, border: '1px solid #ccc', borderRadius: '4px' }}>
                                <Typography variant="subtitle1">Wymagania dotyczące hasła:</Typography>
                                <Typography variant="body2" color={passwordValid.length ? "green" : "red"}>
                                    {passwordValid.length ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                                    Co najmniej 6 znaków
                                </Typography>
                                <Typography variant="body2" color={passwordValid.digit ? "green" : "red"}>
                                    {passwordValid.digit ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                                    Przynajmniej jedna cyfra
                                </Typography>
                                <Typography variant="body2" color={passwordValid.special ? "green" : "red"}>
                                    {passwordValid.special ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                                    Przynajmniej jeden znak specjalny
                                </Typography>
                                <Typography variant="body2" color={passwordValid.uppercase ? "green" : "red"}>
                                    {passwordValid.uppercase ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                                    Przynajmniej jedna duża litera
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                    <Box mt={2}>
                        <Button variant="contained" color="primary" fullWidth onClick={handleRegisterWaiter}>
                            Zarejestruj Kelnera
                        </Button>
                    </Box>
                </Paper>
            )}

            <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
                <DialogTitle>Usuń Kelnera</DialogTitle>
                <DialogContent>
                    <Typography>Czy na pewno chcesz usunąć tego kelnera?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteDialogClose}>Anuluj</Button>
                    <Button onClick={handleDeleteWaiterConfirm} color="error">
                        Usuń
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ManageWaiters;